import {isPlatformBrowser} from "@angular/common";
import {provideHttpClient, withInterceptors} from "@angular/common/http";
import {
  type ApplicationConfig,
  inject,
  injectAsync,
  LOCALE_ID,
  PLATFORM_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from "@angular/core";
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
  withI18nSupport,
} from "@angular/platform-browser";
import {provideRouter, withComponentInputBinding, withInMemoryScrolling, withViewTransitions} from "@angular/router";
import {NgbDateAdapter, NgbDateParserFormatter, NgbTimeAdapter} from "@ng-bootstrap/ng-bootstrap";
import {firstValueFrom} from "rxjs";
import {type IWebAppConfig, WEB_APP_CONFIG} from "../common/app-config";
import {routes} from "./app.routes";
import {LuxonNgbDateAdapter, LuxonNgbDateParserFormatter, LuxonNgbTimeAdapter} from "./helpers/LuxonNgbDateAdapter";
import {setDefaultLocale} from "./helpers/ValueFormatter";
import {authenticationInterceptorFn} from "./services/interceptors/authentication-interceptor";
import {errorInterceptorFn} from "./services/interceptors/error-interceptor";
import {timezoneInterceptorFn} from "./services/interceptors/timezone-interceptor";

let loadedWebAppConfig: IWebAppConfig;

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes,
      // withRouterConfig({paramsInheritanceStrategy: "always"}),
      // withPreloading(PreloadAllModules), // TODO: Does that make sense?
      withComponentInputBinding(),
      withInMemoryScrolling({
        anchorScrolling: "enabled",
        scrollPositionRestoration: "enabled",
      }),
      withViewTransitions()),
    provideClientHydration(
      withEventReplay(),
      withI18nSupport(),
      withHttpTransferCacheOptions({
        // includeHeaders: [],
        includePostRequests: true,
        includeRequestsWithAuthHeaders: true,
        includeRequestsWithCredentials: true,
      })),
    provideHttpClient(withInterceptors([
      authenticationInterceptorFn, timezoneInterceptorFn, errorInterceptorFn,
    ])),
    provideAppInitializer(async (): Promise<void> => {
      setDefaultLocale(inject(LOCALE_ID));
      if (isPlatformBrowser(inject(PLATFORM_ID))) { // web app config provided in app.config.server.ts
        const webApi = await injectAsync(() => import("./services/api/web/web-api").then(m => m.WebApi))();
        loadedWebAppConfig = await firstValueFrom(webApi.getAppConfig());
      }
    }),
    {
      provide: LOCALE_ID,
      useFactory: (): string => {
        // Unfortunately, LOCALE_ID is not provided by a parent injector. This is why we include `optional: true` here.
        // However, `$localize.locale` is usually defined in these cases (except for translation extraction).
        const locale = inject(LOCALE_ID, {skipSelf: true, optional: true}) ?? $localize.locale;
        if (locale === "en") return "en-GB";
        // Should it still be undefined, fall back to en-US, which is the Angular and development default.
        return locale ?? "en-US";
      },
    },
    {
      provide: WEB_APP_CONFIG,
      useFactory: (platformId: object): IWebAppConfig => {
        if (isPlatformBrowser(platformId)) return loadedWebAppConfig;
        // provided in app.config.server.ts
        return inject(WEB_APP_CONFIG, {skipSelf: true});
      },
      deps: [PLATFORM_ID],
    },
    {provide: NgbDateAdapter, useExisting: LuxonNgbDateAdapter},
    {provide: NgbTimeAdapter, useExisting: LuxonNgbTimeAdapter},
    {provide: NgbDateParserFormatter, useExisting: LuxonNgbDateParserFormatter},
  ],
};
